import { userExperienceTracker } from './userExperienceTracker';
import { smartCacheManager } from './smartCacheManager';
import { backgroundSyncService } from './backgroundSyncService';

interface PreloadRule {
  trigger: string; // Current page pattern
  targets: string[]; // Pages to preload
  probability: number; // 0-1
  conditions?: {
    timeOnPage?: number; // Minimum time on current page
    userType?: string;
    timeOfDay?: [number, number]; // Hour range
  };
}

interface UserBehaviorPattern {
  sequence: string[];
  frequency: number;
  avgTime: number;
  lastSeen: number;
}

class PredictivePreloader {
  private preloadRules: PreloadRule[] = [];
  private userPatterns: UserBehaviorPattern[] = [];
  private preloadedRoutes = new Set<string>();
  private isEnabled = true;

  constructor() {
    this.initializeDefaultRules();
    this.startPatternAnalysis();
  }

  private initializeDefaultRules(): void {
    this.preloadRules = [
      {
        trigger: '/anasayfa',
        targets: ['/onemli-konu-secimi', '/veri-toplama-sureci'],
        probability: 0.7,
        conditions: { timeOnPage: 5000 }
      },
      {
        trigger: '/onemli-konu-secimi',
        targets: ['/veri-toplama-sureci', '/anket-hazirla'],
        probability: 0.8,
        conditions: { timeOnPage: 10000 }
      },
      {
        trigger: '/veri-toplama-sureci',
        targets: ['/cifte-onemlilik-matrisi', '/tsrs'],
        probability: 0.6,
        conditions: { timeOnPage: 15000 }
      },
      {
        trigger: '/admin/dashboard',
        targets: ['/admin/users', '/admin/companies'],
        probability: 0.5,
        conditions: { timeOnPage: 3000 }
      }
    ];
  }

  private startPatternAnalysis(): void {
    setInterval(() => {
      this.analyzeUserPatterns();
      this.executePreloading();
    }, 30000); // Every 30 seconds
  }

  private analyzeUserPatterns(): void {
    const journey = userExperienceTracker.getUserJourney();
    const navigationActions = journey.filter(a => a.type === 'navigation');
    
    if (navigationActions.length < 3) return;

    // Extract sequences of 3 pages
    for (let i = 0; i < navigationActions.length - 2; i++) {
      const sequence = navigationActions.slice(i, i + 3).map(a => a.page);
      const timeSpent = navigationActions[i + 2].timestamp - navigationActions[i].timestamp;
      
      this.updatePattern(sequence, timeSpent);
    }

    // Clean old patterns
    const oneWeekAgo = Date.now() - 7 * 24 * 60 * 60 * 1000;
    this.userPatterns = this.userPatterns.filter(p => p.lastSeen > oneWeekAgo);
  }

  private updatePattern(sequence: string[], timeSpent: number): void {
    const sequenceKey = sequence.join(' -> ');
    const existing = this.userPatterns.find(p => p.sequence.join(' -> ') === sequenceKey);
    
    if (existing) {
      existing.frequency++;
      existing.avgTime = (existing.avgTime + timeSpent) / 2;
      existing.lastSeen = Date.now();
    } else {
      this.userPatterns.push({
        sequence,
        frequency: 1,
        avgTime: timeSpent,
        lastSeen: Date.now()
      });
    }
  }

  private executePreloading(): void {
    if (!this.isEnabled) return;

    const currentPage = window.location.pathname;
    const timeOnCurrentPage = this.getTimeOnCurrentPage();

    // Rule-based preloading
    this.preloadRules.forEach(rule => {
      if (this.matchesPattern(currentPage, rule.trigger) && 
          this.meetsConditions(rule.conditions, timeOnCurrentPage)) {
        
        if (Math.random() < rule.probability) {
          rule.targets.forEach(target => this.preloadRoute(target));
        }
      }
    });

    // Pattern-based preloading
    this.predictFromPatterns(currentPage).forEach(prediction => {
      if (prediction.confidence > 0.6) {
        this.preloadRoute(prediction.route);
      }
    });
  }

  private predictFromPatterns(currentPage: string): { route: string; confidence: number }[] {
    const predictions: { route: string; confidence: number }[] = [];
    
    // Find patterns that include current page
    const relevantPatterns = this.userPatterns.filter(p => 
      p.sequence.includes(currentPage) && p.frequency >= 2
    );

    relevantPatterns.forEach(pattern => {
      const currentIndex = pattern.sequence.indexOf(currentPage);
      if (currentIndex < pattern.sequence.length - 1) {
        const nextPage = pattern.sequence[currentIndex + 1];
        const confidence = Math.min(0.9, pattern.frequency / 10);
        
        predictions.push({ route: nextPage, confidence });
      }
    });

    return predictions.sort((a, b) => b.confidence - a.confidence);
  }

  private async preloadRoute(route: string): Promise<void> {
    if (this.preloadedRoutes.has(route)) return;

    this.preloadedRoutes.add(route);
    
    try {
      // Preload critical data for the route
      const preloadTasks = this.getPreloadTasksForRoute(route);
      
      preloadTasks.forEach(task => {
        backgroundSyncService.addTask('data_fetch', task, 'low');
      });

      // Cache route metadata
      smartCacheManager.setPageCache(`preload_${route}`, {
        preloadedAt: Date.now(),
        route
      }, { maxAge: 300000 }); // 5 minutes

      console.log(`Preloaded route: ${route}`);
    } catch (error) {
      console.warn(`Failed to preload route: ${route}`, error);
      this.preloadedRoutes.delete(route);
    }
  }

  private getPreloadTasksForRoute(route: string): any[] {
    // Disable preload tasks to prevent 404 errors
    return [];
  }

  private matchesPattern(current: string, pattern: string): boolean {
    if (pattern.includes('*')) {
      const regex = new RegExp(pattern.replace(/\*/g, '.*'));
      return regex.test(current);
    }
    return current === pattern;
  }

  private meetsConditions(conditions: PreloadRule['conditions'], timeOnPage: number): boolean {
    if (!conditions) return true;

    if (conditions.timeOnPage && timeOnPage < conditions.timeOnPage) {
      return false;
    }

    if (conditions.timeOfDay) {
      const hour = new Date().getHours();
      const [start, end] = conditions.timeOfDay;
      if (hour < start || hour > end) return false;
    }

    return true;
  }

  private getTimeOnCurrentPage(): number {
    const journey = userExperienceTracker.getUserJourney();
    const lastNavigation = journey
      .filter(a => a.type === 'navigation')
      .pop();
    
    return lastNavigation ? Date.now() - lastNavigation.timestamp : 0;
  }

  // Public methods
  addPreloadRule(rule: PreloadRule): void {
    this.preloadRules.push(rule);
  }

  removePreloadRule(trigger: string): void {
    this.preloadRules = this.preloadRules.filter(r => r.trigger !== trigger);
  }

  getPreloadStats() {
    return {
      rulesCount: this.preloadRules.length,
      patternsCount: this.userPatterns.length,
      preloadedRoutes: Array.from(this.preloadedRoutes),
      isEnabled: this.isEnabled
    };
  }

  enable(): void {
    this.isEnabled = true;
  }

  disable(): void {
    this.isEnabled = false;
    this.preloadedRoutes.clear();
  }

  clearPreloadCache(): void {
    this.preloadedRoutes.clear();
    // Clear preload cache entries
    Object.keys(localStorage).forEach(key => {
      if (key.startsWith('bg_cache_')) {
        localStorage.removeItem(key);
      }
    });
  }
}

export const predictivePreloader = new PredictivePreloader();
export type { PreloadRule, UserBehaviorPattern };
